import { Link, useLocation } from 'react-router';
import { Compass } from 'lucide-react';
import { PageContainer } from '../components/layout/AppShell.js';
import { Card } from '../components/ui.js';

/**
 * A real page, not a toast.
 *
 * It is also what a 404 from the API renders: a course or session slug that
 * does not exist — or that does exist but was never released to this reader —
 * is the same dead end as a mistyped URL, and both deserve a route back rather
 * than an error code.
 */
export function NotFoundPage({
  title = 'We could not find that page',
  description,
}: {
  title?: string;
  description?: string;
} = {}) {
  const location = useLocation();

  return (
    <PageContainer>
      <Card className="max-w-2xl mx-auto mt-6 p-8 text-center">
        <span
          aria-hidden
          className="inline-grid place-items-center h-12 w-12 rounded-card bg-surface-2 border border-line text-ink-muted mx-auto"
        >
          <Compass size={22} />
        </span>
        <h1 className="text-2xl font-semibold tracking-tight text-ink mt-4">{title}</h1>
        <p className="text-sm text-ink-muted mt-2 leading-relaxed max-w-prose mx-auto">
          {description ??
            'The link may be out of date, the address may have a typo in it, or the course it pointed at may not be released to you. Nothing is broken on your side.'}
        </p>
        <p className="text-xs text-ink-subtle mt-3 break-all">
          <span className="font-medium">You asked for:</span> {location.pathname}
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-control border border-brand bg-brand px-5 py-2.5 text-sm font-semibold text-brand-ink hover:bg-brand-strong hover:border-brand-strong transition-colors duration-150 ease-out"
          >
            Back to my learning
          </Link>
        </div>
      </Card>
    </PageContainer>
  );
}
